import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from '@emilegal/emi-fe-common';

export interface Party { name: string; variants: string[] }
export interface Identity { client: Party; employers: Party[]; relatedParties?: Party[]; related_parties?: Party[] }
export type Role = 'owner' | 'editor' | 'viewer';
export type RunState = 'running' | 'waiting' | 'completed' | 'failed' | 'cancelled';
// How agents may use a file in future runs; the project keeps every file either way.
export type UsePolicy = 'available' | 'internal_reference' | 'do_not_process';

// An agent the caller may run; its kind decides the date label, options and steps.
export interface ProjectAgent { agent_id: string; name: string; kind: string; description?: string | null; created_at?: string | null }
// Active cases are not archived; inactive ones are.
export type CaseStatusFilter = 'all' | 'active' | 'inactive';
export interface Person { user_id: string; name: string; email?: string | null }
export interface ProjectRow {
  project_id: string; name: string; client: string; matter_number: string | null; practice_area: string | null;
  role: Role; pinned: boolean; members: number; files: number;
  // Up to five members, owners first.
  team: Person[];
  archived_at: string | null; created_at: string; last_activity: string;
  last_run: { execution_id: string; status: RunState; created_at: string; finished_at: string | null;
    kind: string; agent_name: string; draft_available: boolean | null } | null;
}
export interface ProjectList {
  default_as_of: string; agents: ProjectAgent[]; total: number; page: number; page_size: number;
  status: CaseStatusFilter; projects: ProjectRow[];
}
// What the firm files a case under; none of it is read by the agents.
export interface CaseDetails { matterNumber?: string | null; practiceArea?: string | null; leadAttorney?: string | null; jurisdiction?: string | null }
export interface CreateCase { name: string; identity: Identity; details?: CaseDetails; members?: { userUid: string; role: Role }[] }
export interface ProjectFile {
  file_id: string; name: string; size: number; sha256: string; uploaded_by: string; uploaded_at: string;
  removed_at: string | null; use_policy: UsePolicy;
}
export interface Decision {
  decision_id: number; kind: 'exclude' | 'unpark' | 'reclassify'; sha256: string; document_type: string | null;
  note: string; by: string; at: string; source_execution_id: string | null; workflow_kind: string;
}
export interface ProjectRunRow {
  execution_id: string; status: RunState; as_of: string; kind: string; agent_id: string | null; agent_name: string;
  started_by: string; created_at: string; finished_at: string | null; archived_at: string | null;
  files_changed: boolean; draft_available: boolean | null;
}
export interface ProjectDetail {
  project_id: string; name: string; client: string; identity: Identity; role: Role; archived_at: string | null;
  details: { matter_number: string | null; practice_area: string | null; lead_attorney: string | null; jurisdiction: string | null };
  // The caller's own choices for this case.
  me: { pinned: boolean; email_notifications: boolean };
  default_as_of: string;
  limits: { max_file_bytes: number; max_files: number; max_project_bytes: number };
  members: { user_id: string; role: Role; name: string; email: string | null }[];
  files: ProjectFile[]; decisions: Decision[]; runs: ProjectRunRow[];
}
export interface RunnableAgents { can_start: boolean; reason: string | null; agents: ProjectAgent[] }
export interface StartRequest { asOf: string; agentId: string; options?: LetterOptions }
export interface Stage {
  stage: string; label: string; status: 'running' | 'completed' | 'incomplete' | 'failed';
  started_at: string; finished_at: string | null; seconds: number | null; cost_usd: number | null; detail: string | null;
}
export interface RunSummary {
  as_of: string; processing_version: string; registry_version: string | null;
  totals: { input_tokens?: number; output_tokens?: number; cost_usd: number | null; minutes: number };
  incomplete_stages: string[]; stages: Stage[]; versions: Record<string, unknown>;
  error_code?: string | null; reasons?: string[]; attention?: string[]; usage_note?: string | null; holds?: string[]; limitations?: string[];
  acknowledged_by?: string | null; identity?: any;
  attempts?: { attempt: number; status: string; error_code: string | null; reasons: string[] }[];
}
export interface LetterOptions { includeWageHour: boolean; instructions: string; }
export interface RunFile { file_uid: string; name: string; sha256: string; size: number; use_policy?: UsePolicy }
export interface RunStatus {
  execution_id: string; project_id: string; project_name: string; role: Role; status: RunState; phase: string;
  // What the run was started as, frozen with it; never the project's or the agent's current state.
  kind: string; agent_id: string | null; agent_name: string;
  letter_options?: { include_wage_hour: boolean; instructions: string } | null;
  submission_pending: boolean; as_of: string; started_by: string | null; created_at: string;
  finished_at: string | null; cancelled_at: string | null; archived_at: string | null; queue_revision: string | null;
  receipt: { queue_revision: string; by: string; acknowledged_at: string; decisions: unknown[]; identity?: Identity } | null;
  live: { status: string; current_step: string; stages: Stage[]; submitted_at: string; retrying: boolean; attempt?: number;
    delivering: boolean; error: string | null } | null;
  run: (RunSummary & { error?: string; run?: RunSummary; can_retry?: boolean }) | null;
  files_used: RunFile[];
  // Listed for the record, never sent for processing. Null for runs from before use policies.
  files_withheld: RunFile[] | null;
  decisions_used: { kind: string; sha256: string; document_type: string | null; note: string; by: string }[];
  files_changed: boolean; approved: false;
  outputs: { kind: string; label: string; size: number; sha256: string; content_type: string }[];
}
export interface QueueDocument {
  sha256: string; name: string; aliases: string[]; document_type: string; tier: string;
  state: 'active' | 'parked' | 'excluded'; in_queue: boolean; state_reasons: string[]; queue_reasons: string[];
  summary: string; confidence: number | null; pages: number | null; conversion_status: string; vision_read: boolean;
  prior_decision: { kind: string; document_type: string | null; note: string; by: string } | null;
}
export interface Queue {
  identity?: Identity | null;
  identity_sources?: { kind: string; name: string; sha256: string; document: string; quote: string; client_quote: string }[];
  queue_revision: string; counts: Record<string, number>; documents: QueueDocument[];
  stale_decisions: { kind: string; sha256: string; by: string; note: string }[];
  document_types: { name: string; definition: string }[]; vision: { enabled: boolean };
}
export interface DecisionInput { kind: 'exclude' | 'unpark' | 'reclassify'; sha256: string; documentType?: string | null; note?: string }

@Injectable({ providedIn: 'root' })
export class ComplaintService extends BaseService {
  private base = 'tenantagentexecute/complaint';
  // Cases are called projects in the API.
  projects(query: { search?: string; status: CaseStatusFilter; page: number; pageSize: number }) {
    let params = new HttpParams().set('status', query.status).set('page', query.page).set('pageSize', query.pageSize);
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    return this.get<ProjectList>(`${this.base}/projects`, params);
  }
  // The creator becomes the owner; `members` are the other people the case is shared with.
  create(body: CreateCase) { return this.post<ProjectDetail>(`${this.base}/projects`, body); }
  project(id: string) { return this.get<ProjectDetail>(`${this.base}/projects/${id}`); }
  agents(id: string) { return this.get<RunnableAgents>(`${this.base}/projects/${id}/agents`); }
  update(id: string, body: { name?: string; identity?: Identity; details?: CaseDetails }) { return this.put<ProjectDetail>(`${this.base}/projects/${id}`, body); }
  archive(id: string, archived: boolean) { return this.put<{ archived_at: string | null }>(`${this.base}/projects/${id}/archive`, { archived }); }
  // The caller's pinned cases, for the sidebar.
  pinned() { return this.get<{ projects: { project_id: string; name: string }[] }>(`${this.base}/projects/pinned`); }
  pin(id: string, pinned: boolean) { return this.put<{ project_id: string; pinned: boolean }>(`${this.base}/projects/${id}/pin`, { pinned }); }
  // Only the caller's own setting: whether run emails for this case reach them.
  settings(id: string, emailNotifications: boolean) {
    return this.put<{ project_id: string; email_notifications: boolean }>(`${this.base}/projects/${id}/settings`, { emailNotifications });
  }
  // The firm's people other than the caller, to share a new case with.
  firmCandidates() { return this.get<{ candidates: Person[] }>(`${this.base}/projects/candidates`); }
  // People who can be added to this case (owners only).
  candidates(id: string) { return this.get<{ candidates: Person[] }>(`${this.base}/projects/${id}/candidates`); }
  addMember(id: string, userUid: string, role: Role) { return this.post<ProjectDetail>(`${this.base}/projects/${id}/members`, { userUid, role }); }
  changeMember(id: string, user: string, role: Role) { return this.put<ProjectDetail>(`${this.base}/projects/${id}/members/${user}`, { role }); }
  removeMember(id: string, user: string) { return this.delete<ProjectDetail>(`${this.base}/projects/${id}/members/${user}`); }
  uploadUrl(id: string, fileName: string, fileSize: number, sha256: string) {
    return this.post<{ url: string; fields: Record<string, string> }>(`${this.base}/projects/${id}/files/upload-url`, { fileName, fileSize, sha256 });
  }
  register(id: string, key: string, fileName: string, fileSize: number, sha256: string) {
    return this.post<{ file_id: string; state: string }>(`${this.base}/projects/${id}/files`, { key, fileName, fileSize, sha256 });
  }
  removeFile(id: string, file: string) { return this.delete<unknown>(`${this.base}/projects/${id}/files/${file}`); }
  setFilePolicy(id: string, file: string, policy: UsePolicy) {
    return this.put<{ file_id: string; use_policy: UsePolicy }>(`${this.base}/projects/${id}/files/${file}/policy`, { policy });
  }
  // A short-lived attachment link to the original upload, also for files removed from future runs.
  downloadFile(id: string, file: string) { return this.get<{ url: string; fileName: string }>(`${this.base}/projects/${id}/files/${file}/download`); }
  removeDecision(id: string, decision: number) { return this.delete<unknown>(`${this.base}/projects/${id}/decisions/${decision}`); }
  // Options are a demand-letter choice; an existing run keeps the agent, date and options it was started with.
  start(id: string, execution: string, request: StartRequest) {
    const { asOf, agentId, options } = request;
    return this.put<RunStatus>(`${this.base}/projects/${id}/runs/${execution}`, options ? { asOf, agentId, options } : { asOf, agentId });
  }
  run(execution: string) { return this.get<RunStatus>(`${this.base}/runs/${execution}`); }
  queue(execution: string) { return this.get<Queue>(`${this.base}/runs/${execution}/queue`); }
  acknowledge(execution: string, queueRevision: string, decisions: DecisionInput[], identity?: Identity) {
    return this.post<{ receipt: unknown }>(`${this.base}/runs/${execution}/acknowledge`, { queueRevision, decisions, identity });
  }
  cancel(execution: string) { return this.post<RunStatus>(`${this.base}/runs/${execution}/cancel`); }
  archiveRun(execution: string, archived: boolean) { return this.put<{ archived_at: string | null }>(`${this.base}/runs/${execution}/archive`, { archived }); }
  retry(execution: string) { return this.post<RunStatus>(`${this.base}/runs/${execution}/retry`); }
  download(execution: string, kind: string) { return this.get<{ url: string; fileName: string }>(`${this.base}/runs/${execution}/outputs/${kind}/download`); }
  preview(execution: string, kind: string) { return this.getBlobFromBE(`${this.base}/runs/${execution}/outputs/${kind}/preview`); }
}
